import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { DocumentService } from "../../../../services/document/document.service";
import { useSearchParams } from "../../../../hook/use-search-params";
import { useMemo } from "react";
import { EmployeeService } from "../../../../services/department-viags/employee-viags.service";
import { StringHelper } from "../../../../helper";

const removeElements = (arr: any) => {
  return Array.from(new Set(arr.map((obj: any) => obj.email))).map((email) =>
    arr.find((obj: any) => obj.email === email),
  );
};

const isRead = (arr: any[], userMail: any): boolean => {
  return arr.findIndex((e: any) => e.email.toLowerCase() === userMail.toLowerCase()) > -1;
};

const positionFilters = ["-TP", "-PP", "-TTT", "-PTT"];

const isCBP = (jobTitle?: string): boolean => {
  if (!jobTitle) return false;
  return jobTitle.split("-").some((s) => positionFilters.includes(s));
};

export const usePublicDocumentInfo = () => {
  const params = useParams<{ id: string }>();
  const { searchParams } = useSearchParams();
  const docId = searchParams.get("docId");

  const id = params.id || docId;

  const { data, isLoading, isFetched } = useQuery({
    queryKey: ["document-public-info", id],
    queryFn: () => DocumentService.getPublicDocumentInfoFromWorkingDocument(id),
    enabled: !!id,
  });

  const readPeople = useMemo(() => {
    return !data?.docReaders ? [] : [...data?.docReaders];
  }, [data?.docReaders]);

  const docReaders = useMemo(() => {
    return readPeople?.filter((item) => item.email);
  }, [readPeople]);

  const docEndUsers = useMemo(() => {
    const endUsers = !data?.fileAssignedTo ? [] : JSON.parse(data?.fileAssignedTo);
    const filteredArr = removeElements(endUsers);

    return filteredArr.filter(
      (item) => item.email && !item.email.toLowerCase().startsWith("qltl_gr") && item.jobTitle !== "TKDC",
    );
  }, [data?.fileAssignedTo]);

  const groupEmails: any = useMemo(() => {
    const endUsers = !data?.fileAssignedTo ? [] : JSON.parse(data?.fileAssignedTo);

    return endUsers.filter((user: any) => user.email && user.jobTitle === "TKDC").map((e: any) => e.email);
  }, [data?.fileAssignedTo]);

  const { data: nonEmailUsers } = useQuery({
    queryKey: ["non-email-users", groupEmails],
    queryFn: () => EmployeeService.getListNoneEmail(groupEmails),
    enabled: groupEmails.length > 0,
  });

  const finalReadUser = useMemo(() => {
    const listDocReader = docReaders.filter(
      (user: { email: string }) =>
        !docEndUsers.some((readUser: { email: string }) => readUser.email.toLowerCase() === user.email.toLowerCase()),
    );

    const listNonEmailUser = (nonEmailUsers ?? []).filter(
      (s) => !listDocReader.some((reader: any) => reader.id?.toLowerCase() === s.id?.toLowerCase()),
    );

    listDocReader?.forEach((s) => {
      const user: any = nonEmailUsers?.find((r: any) => r.id === s.id);

      s.department = user?.department;
      s.pDeptCode = user?.pDeptCode ?? "";
    });

    const finalArr = [...docEndUsers, ...listDocReader, ...listNonEmailUser];
    return finalArr.map((e: any, index) => ({
      index: index + 1,
      employeeId: e.Id || e.id,
      employeeName: e.displayName || e.name,
      department: StringHelper.dashSplit(e.departmentCode)[0] ?? "",
      pDeptCode: e.departmentCode ? `${(e.departmentCode || e.unit)} ${isCBP(e.jobTitle) ? "-CBP" : ""}` : "",
      read: isRead(readPeople, e.email),
      note: e.note,
    }));
  }, [docEndUsers, docReaders, nonEmailUsers, readPeople]);

  const lisDepartmentId = useMemo(() => {
    const fileAssignedTo = !data?.fileAssignedTo ? [] : JSON.parse(data.fileAssignedTo);
    return fileAssignedTo.map((e: { departmentId: number }) => e.departmentId);
  }, [data?.fileAssignedTo]);

  const listUserId = useMemo(() => {
    const fileAssignedTo = !data?.fileAssignedTo ? [] : JSON.parse(data.fileAssignedTo);
    return fileAssignedTo.map((e: { Id: number }) => e.Id);
  }, [data?.fileAssignedTo]);

  const totalUser = useMemo(() => finalReadUser?.length, [finalReadUser?.length]);

  const countReadUser = useMemo(() => finalReadUser?.filter((e) => e.read).length, [finalReadUser]);

  return { data, isLoading, finalReadUser, countReadUser, totalUser, isFetched, lisDepartmentId, listUserId };
};
